import React from 'react';
import { X } from 'lucide-react';

import { AppModal } from '../../../components/AppModal';
import { formatEuro } from '../utils/offerDocumentUtils';
import {
  documentTypeLabelDe,
  deductionRowMatchesCurrentDocument,
  isDeductionDisplayRow,
  isSelectableDeductionRow,
  mapInvoiceRowToDeductionCandidate,
  selectableDeductionDisabledReason,
  type CurrentDeductionDocument,
  type DeductionInvoiceRow,
} from '../utils/finalInvoiceDeduction';
import type { PaidPartialInvoice } from '../utils/finalInvoiceTotals';
import { InlineLoadingState } from '../../../components/LoadingSpinner';

type FinalInvoiceDeductionPickerModalProps = {
  open: boolean;
  onClose: () => void;
  organizationId?: string | null;
  contactId?: string | number | null;
  currentOfferingId?: string | null;
  currentDocument?: CurrentDeductionDocument | null;
  fallbackVatRatePercent: number;
  selectedIds: string[];
  onConfirm: (selectedIds: string[], candidates: PaidPartialInvoice[]) => void;
};

function formatDateDe(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE');
}

function workflowStatusLabel(row: DeductionInvoiceRow): string {
  const raw = String(row.status ?? 'open').trim().toLowerCase();
  if (row.document_type === 'invoice_correction' && raw === 'paid') return 'Ausbezahlt';
  if (row.document_type === 'credit_note' && raw === 'paid') return 'Verschickt';
  if (raw === 'cancelled') return 'Storniert';
  const map: Record<string, string> = {
    open: 'Offen',
    paid: 'Bezahlt',
    overdue: 'Überfällig',
    draft: 'Entwurf',
    extraction_pending: 'Offen',
    extraction_failed: 'Fehler',
    rejected: 'Abgelehnt',
    cancelled: 'Storniert',
  };
  return map[raw] ?? row.status ?? 'Offen';
}

function projectLabel(row: DeductionInvoiceRow): string {
  const leadId = row.lead_display_id?.trim();
  const offeringId = row.offering_display_id?.trim();
  const title = row.lead_title?.trim();
  if (leadId && offeringId) return `${leadId} · ${offeringId}`;
  if (leadId && title) return `${leadId} · ${title}`;
  return leadId || offeringId || title || '—';
}

export const FinalInvoiceDeductionPickerModal: React.FC<FinalInvoiceDeductionPickerModalProps> = ({
  open,
  onClose,
  organizationId,
  contactId,
  currentOfferingId,
  currentDocument = null,
  fallbackVatRatePercent,
  selectedIds,
  onConfirm,
}) => {
  const [rows, setRows] = React.useState<DeductionInvoiceRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [draftSelectedIds, setDraftSelectedIds] = React.useState<string[]>(selectedIds);

  React.useEffect(() => {
    if (!open) return;
    setDraftSelectedIds(selectedIds);
  }, [open, selectedIds]);

  React.useEffect(() => {
    if (!open) return;
    const orgId = organizationId ? String(organizationId).trim() : '';
    const cId = contactId != null ? String(contactId).trim() : '';
    if (!orgId && !cId) {
      setError('Kein Kunde verknüpft — Abschläge können nicht geladen werden.');
      setRows([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = new URLSearchParams({
          type: 'outgoing',
          status: 'all',
          per_page: '100',
          sort: 'issue_date',
          dir: 'desc',
        });
        if (orgId) params.set('organization_id', orgId);
        else params.set('contact_id', cId);

        const res = await fetch(`/api/invoices?${params.toString()}`, { credentials: 'include' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? 'Rechnungen konnten nicht geladen werden.');
        }
        const json = (await res.json()) as { items?: DeductionInvoiceRow[] };
        if (cancelled) return;
        const items = Array.isArray(json.items) ? json.items : [];
        setRows(items.filter(isDeductionDisplayRow));
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setError(e instanceof Error ? e.message : 'Rechnungen konnten nicht geladen werden.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, organizationId, contactId]);

  const selectableIds = React.useMemo(
    () => rows.filter(isSelectableDeductionRow).map((r) => String(r.id)),
    [rows],
  );

  const draftSelectedSet = React.useMemo(() => new Set(draftSelectedIds), [draftSelectedIds]);

  const toggleId = (id: string) => {
    setDraftSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAll = () => setDraftSelectedIds([...selectableIds]);
  const selectNone = () => setDraftSelectedIds([]);

  const handleConfirm = () => {
    const candidates = rows
      .filter(isSelectableDeductionRow)
      .map((row) => mapInvoiceRowToDeductionCandidate(row, fallbackVatRatePercent));
    const validIds = draftSelectedIds.filter((id) => selectableIds.includes(id));
    onConfirm(validIds, candidates);
    onClose();
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      overlayClassName="z-[272]"
      panelClassName="md:max-w-6xl"
      ariaLabelledBy="final-invoice-deduction-picker-title"
    >
      <div className="flex flex-col h-full min-h-0">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-app-border shrink-0">
          <h2 id="final-invoice-deduction-picker-title" className="text-sm font-semibold text-app-text">
            Abschlag auswählen
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center p-1 rounded-md text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-2 text-[11px] text-app-text-muted border-b border-app-border shrink-0">
          Alle Rechnungen des Kunden. Gestellte Abschlagsrechnungen (offen, überfällig oder bezahlt) können abgezogen werden.
        </div>

        <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
          {loading ? (
            <InlineLoadingState label="Rechnungen werden geladen…" className="py-8" />
          ) : error ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {error}
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-app-text-subtle py-8 text-center">Keine Rechnungen gefunden.</p>
          ) : (
            <table className="w-full border-collapse text-[11px] text-app-text">
              <thead>
                <tr className="bg-app-surface-muted border-b border-app-border">
                  <th className="w-8 px-2 py-1.5" />
                  <th className="text-left font-semibold px-2 py-1.5">Art</th>
                  <th className="text-left font-semibold px-2 py-1.5">Rechnungsnr.</th>
                  <th className="text-left font-semibold px-2 py-1.5">Projekt</th>
                  <th className="text-left font-semibold px-2 py-1.5">Baustelle</th>
                  <th className="text-left font-semibold px-2 py-1.5">Datum</th>
                  <th className="text-right font-semibold px-2 py-1.5">Netto</th>
                  <th className="text-right font-semibold px-2 py-1.5">USt.</th>
                  <th className="text-right font-semibold px-2 py-1.5">Brutto</th>
                  <th className="text-left font-semibold px-2 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  // Zuletzt aus diesem Entwurf gestellt: als Abzug für die nächste Rechnung wählbar.
                  const isCurrent = deductionRowMatchesCurrentDocument(row, currentDocument);
                  const selectable = isSelectableDeductionRow(row);
                  const mapped = mapInvoiceRowToDeductionCandidate(row, fallbackVatRatePercent);
                  const disabledReason = selectableDeductionDisabledReason(row);
                  const isCurrentOffering =
                    currentOfferingId != null &&
                    row.offering_id != null &&
                    String(row.offering_id) === String(currentOfferingId);
                  return (
                    <tr key={row.id} className="border-b border-app-border hover:bg-app-hover/80">
                      <td className="px-2 py-1.5 text-center">
                        <input
                          type="checkbox"
                          checked={draftSelectedSet.has(String(row.id))}
                          disabled={!selectable}
                          title={disabledReason ?? undefined}
                          onChange={() => toggleId(String(row.id))}
                          className="h-3.5 w-3.5 rounded border-app-border-strong text-blue-600 disabled:opacity-40"
                        />
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {documentTypeLabelDe(row.document_type)}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <span>{row.invoice_number || row.reference_id || '—'}</span>
                          {isCurrent ? (
                            <span
                              title="Zuletzt aus diesem Entwurf gestellt. Ausgewählt wird sie in der nächsten Rechnung abgezogen."
                              className="rounded bg-app-surface-muted px-1 py-0.5 text-[9px] font-medium text-app-text-muted border border-app-border"
                            >
                              Zuletzt gestellt
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="inline-flex items-center gap-1.5">
                          <span>{projectLabel(row)}</span>
                          {isCurrentOffering ? (
                            <span className="rounded bg-blue-50 px-1 py-0.5 text-[9px] font-medium text-blue-700 border border-blue-200">
                              Aktuelles Angebot
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 max-w-[16rem] break-words">
                        {row.site_address?.trim() || '—'}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{formatDateDe(row.issue_date)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatEuro(mapped.netAmount)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatEuro(mapped.vatAmount)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatEuro(mapped.grossAmount)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{workflowStatusLabel(row)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-app-border shrink-0">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={selectAll}
              disabled={loading || selectableIds.length === 0}
              className="inline-flex items-center rounded border border-blue-300 bg-app-surface px-2.5 py-1 text-[11px] font-normal text-blue-600 transition-colors hover:border-blue-500 hover:text-blue-800 disabled:opacity-40"
            >
              Alle Abschläge
            </button>
            <button
              type="button"
              onClick={selectNone}
              disabled={loading || draftSelectedIds.length === 0}
              className="inline-flex items-center rounded border border-app-border-strong bg-app-surface px-2.5 py-1 text-[11px] font-normal text-app-text-muted transition-colors hover:border-app-border-strong hover:text-app-text disabled:opacity-40"
            >
              Keine
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded border border-app-border-strong bg-app-surface px-3 py-1 text-[11px] font-normal text-app-text-muted transition-colors hover:bg-app-hover"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className="inline-flex items-center rounded border border-blue-600 bg-blue-600 px-3 py-1 text-[11px] font-normal text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
            >
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </AppModal>
  );
};
